import { Injectable } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { ApiClass } from '../../../class/api-class';
import { catchError, filter, map, Observable, of } from 'rxjs';
import { IFlow } from '../../../interfaces/iflow.interface';
import { IWeetrustDocument } from '../../../interfaces/iweetrusth.interface';
import { IResponse } from '../../../interfaces/iresponseArray.interface';
import { environment } from '../../../../../environments/environment';

// Respuesta de POST flowData/document ya normalizada (la misma forma que deja
// postCollection). En los pasos multiarchivo `data` trae el FlowData con
// _id, completed y files.
export interface IRespuestaDocumento {
  error: boolean;
  msg: string;
  data: IFlow | null;
}

// Lo que emite uploadDocumentConProgreso: el avance de la subida del archivo
// (0-100) y, al final, la respuesta del backend.
export type IEventoSubidaDocumento =
  | { tipo: 'progreso'; porcentaje: number }
  | { tipo: 'respuesta'; respuesta: IRespuestaDocumento };

// El colaborador no cumple las reglas de crédito de su nómina (antigüedad o
// edad capturadas): getOffers, calculateOffers y applyOffer responden
// 422 { code: 'NO_ELEGIBLE', message, bloqueos }. No es un error: la pantalla
// de la oferta lo dice con su propio estado.
export const CODIGO_NO_ELEGIBLE = 'NO_ELEGIBLE';

// Un requisito que no se cumple (MIN_ANTIGUITY, MIN_AGE, MAX_AGE…), con el
// mensaje ya escrito para el colaborador.
export interface IBloqueoOferta {
  codigo: string;
  mensaje: string;
}

export interface INoElegible {
  // El `message` del servidor (el primer bloqueo).
  mensaje: string;
  bloqueos: IBloqueoOferta[];
}

// Respuesta de getOffers / calculateOffers / applyOffer: la misma forma que
// deja postCollection y, solo con el 422 NO_ELEGIBLE, `noElegible`.
export interface IRespuestaOferta<T> {
  error: boolean;
  msg: string;
  data: T;
  noElegible?: INoElegible;
}

const MENSAJE_NO_ELEGIBLE = 'Por ahora no cumples con los requisitos para solicitar un crédito.';

const textoDe = (valor: unknown): string => (typeof valor === 'string' ? valor.trim() : '');

// Lee el 422 NO_ELEGIBLE del cuerpo del error (null si es cualquier otro error).
// Acepta los bloqueos con llaves en español o en inglés (codigo/mensaje o
// code/message, como los avisos del motor) y el mensaje en `message` o `msg`.
export function leerNoElegible(cuerpo: any): INoElegible | null {
  if (!cuerpo || typeof cuerpo !== 'object' || cuerpo.code !== CODIGO_NO_ELEGIBLE) return null;
  const bloqueos: IBloqueoOferta[] = (Array.isArray(cuerpo.bloqueos) ? cuerpo.bloqueos : [])
    .map((b: any) => ({ codigo: textoDe(b?.codigo ?? b?.code), mensaje: textoDe(b?.mensaje ?? b?.message) }))
    .filter((b: IBloqueoOferta) => b.mensaje !== '');
  const mensaje = textoDe(cuerpo.message) || textoDe(cuerpo.msg) || bloqueos[0]?.mensaje || MENSAJE_NO_ELEGIBLE;
  return { mensaje, bloqueos };
}

@Injectable({
  providedIn: 'root',
})
export class ApiFlowDataService extends ApiClass {
  constructor() {
    super();
    this.path = 'flowData';
  }

  saveInfo(data: any) {
    return this.postCollection(data, 'form').pipe(
      map((r) => r as { error: boolean; msg: string; data: IFlow })
    );
  }

  uploadDocument(data: any) {
    return this.postCollection(data, 'document').pipe(
      map((r) => r as { error: boolean; msg: string; data: IFlow })
    );
  }

  // Mismo POST que uploadDocument (misma URL que arma postCollection, mismo
  // cuerpo y los mismos interceptores de this.http), pero con el avance de la
  // subida para la barra de la liga. Los errores se normalizan igual que en
  // postCollection, así que nunca revienta: siempre termina en una respuesta.
  uploadDocumentConProgreso(data: FormData): Observable<IEventoSubidaDocumento> {
    return this.http
      .post<IResponse>(environment.apiUrl + this.path + '/document', data, { reportProgress: true, observe: 'events' })
      .pipe(
        map((evento): IEventoSubidaDocumento | null => {
          if (evento.type === HttpEventType.UploadProgress) {
            if (!evento.total) return null;
            return { tipo: 'progreso', porcentaje: Math.min(100, Math.round((100 * evento.loaded) / evento.total)) };
          }
          if (evento.type === HttpEventType.Response) {
            const r = evento.body;
            return {
              tipo: 'respuesta',
              respuesta: r
                ? { error: r.error, msg: r.msg, data: r.data }
                : { error: true, msg: 'Hubo un error de conexión', data: null },
            };
          }
          return null;
        }),
        filter((evento): evento is IEventoSubidaDocumento => evento !== null),
        catchError((e) =>
          of<IEventoSubidaDocumento>({
            tipo: 'respuesta',
            respuesta: {
              msg: e?.error?.msg ?? 'Hubo un error de conexión',
              data: e?.error?.data ?? null,
              error: e?.error?.error ?? true,
            },
          })
        )
      );
  }

  uploadVideo(data: any) {
    return this.postCollection(data, 'video').pipe(map((r) => r as {error: boolean, msg: string; data: IFlow}))
  }

  getIneValidationUrl(data: any) {
    return this.postCollection(data, 'ineValidationUrl').pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            data: { url: string; sessionStatus: string; stageId: string };
          }
      )
    );
  }

  getAccountValidationUrl(data: any) {
    return this.postCollection(data, 'account').pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            data: { url: string; sessionStatus: string; stageId: string };
          }
      )
    );
  }

  getRfc(workerId: string, documentId: string) {
    return this.postCollection({ workerId, documentId }, 'rfc').pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg?: string;
            data?: { rfc?: string };
          }
      )
    );
  }

  getWorkerAccounts(workerId: string) {
    const prev = this.path;
    this.path = 'app-domiciliation/getWorkerAccounts';
    const res = this.postCollection(
      { data: { workerId } },
      '',
      environment.apiDomiciliation
    ).pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            data: any[];
          }
      )
    );
    this.path = prev;
    return res;
  }

  connectAccount(body: { workerId: string; accountNumber: string, rfc: string }) {
    const prev = this.path;
    this.path = 'app-domiciliation/connectAccount';
    const res = this.postCollection(
      { data: body },
      '',
      environment.apiDomiciliation
    ).pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            data: {
              slug?: string;
              account_uuid?: string;
            };
          }
      )
    );
    this.path = prev;
    return res;
  }

  authenticateAccount(body: {
    account_uuid: string;
    slug: string;
    otp: number;
  }) {
    const prev = this.path;
    this.path = 'app-domiciliation/authenticeAccount';
    const res = this.postCollection(
      body,
      '',
      environment.apiDomiciliation
    ).pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            data: any;
          }
      )
    );
    this.path = prev;
    return res;
  }

  updateSessionStatusAndStage(workerId: string, properties: {}, clabe: string) {
    const previousPath = this.path;
    this.path = 'flowData/update-session';

    const request = this.postCollection(
      { workerId, properties, clabe },
      '',
      environment.apiUrl
    ).pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            data: {
              workerId: string;
            };
          }
      )
    );

    this.path = previousPath;

    return request;
  }

  validateIneValidationStatus(data: any) {
    return this.postCollection(data, 'ineValidationStatus').pipe(
      map((r) => r as { error: boolean; msg: string; data: IFlow })
    );
  }

  validateExternalUrl(data: any) {
    return this.postCollection(data, 'external').pipe(
      map((r) => r as { error: boolean; msg: string; data: IFlow })
    );
  }

  getWeetrustDocument(data: any) {
    return this.postCollection(data, 'documentSign').pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            data: {
              weetrustDocument: IWeetrustDocument;
              status: string;
              stageId: string;
            };
          }
      )
    );
  }

  // Mismo POST que postCollection (misma URL, cuerpo e interceptores) y los
  // demás errores se normalizan igual; solo el 422 NO_ELEGIBLE se lee aparte:
  // postCollection se queda con `msg` y perdería `code` y `bloqueos` (sin `msg`
  // en el cuerpo, además, lo diría como «Hubo un error de conexión»).
  private postOferta(data: any, subPath: string): Observable<IRespuestaOferta<any>> {
    return this.http.post<IResponse>(environment.apiUrl + this.path + '/' + subPath, data).pipe(
      map((r) => ({ error: r.error, msg: r.msg, data: r.data })),
      catchError((e) => {
        const noElegible = leerNoElegible(e?.error);
        if (noElegible) return of({ error: true, msg: noElegible.mensaje, data: null, noElegible });
        console.log(e);
        return of({
          msg: e.error.msg ?? 'Hubo un error de conexión',
          data: e.error.data ?? null,
          error: e.error.error ?? true,
        });
      })
    );
  }

  getOffers(data: any) {
    return this.postOferta(data, 'getOffers').pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            noElegible?: INoElegible;
            data: {
              stageId: string;
              loanProperties: {
                loanStatusEnabled: boolean;
                loanAmountAllowed: number;
                loanRate: number;
                period: string;
                minAmount: number;
              };
            };
          }
      )
    );
  }

  calculateOffers(data: any) {
    return this.postOferta(data, 'calculateOffers').pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            noElegible?: INoElegible;
            data: {
              stageId: string;
              offers: { installmentCount: number; installmentAmount: number }[];
            };
          }
      )
    );
  }

  applyOffers(data: any) {
    return this.postOferta(data, 'applyOffer').pipe(
      map(
        (r) =>
          r as {
            error: boolean;
            msg: string;
            noElegible?: INoElegible;
            data: { stageId: string };
          }
      )
    );
  }
}
